import React, { useState, useRef, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IoCloseSharp, IoColorWandSharp, IoRepeatOutline, IoRefreshOutline, IoExpandSharp, IoContractSharp, IoCropOutline } from 'react-icons/io5';
import ReactCrop from 'react-image-crop';
import cogoToast from 'cogo-toast';
import _ from 'lodash';
import Modal from '../../../General/Modal';
import { prettifyImage, flipImage, cropImage } from '../../../../api/images';
import Loader from '../../../General/Loader';
import 'react-image-crop/dist/ReactCrop.css';
import { getSmartImage } from '../../../../helpers/helpers';

const useQueue = handleRequestChange => {
  const [queue, setQueue] = useState([]);
  const queueRef = useRef(queue);
  queueRef.current = queue;

  const isProcessingRef = useRef(false);

  const handlerRef = useRef(handleRequestChange);
  handlerRef.current = handleRequestChange;

  const process = async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    try {
      while (queueRef.current.length) {
        const item = queueRef.current[0];
        await handlerRef.current(item);

        // Remove processed item and notify UI
        queueRef.current.shift();
        setQueue([...queueRef.current]);
      }
    } finally {
      isProcessingRef.current = false;
    }
  };

  const add = useCallback(item => {
    if (!queueRef.current.includes(item)) {
      queueRef.current.push(item);
      setQueue([...queueRef.current]);
      process();
    }
  }, []);

  const remove = useCallback(item => {
    const idx = queueRef.current.indexOf(item);
    if (idx !== -1) {
      queueRef.current.splice(idx, 1);
      setQueue([...queueRef.current]);
    }
  }, []);

  const contains = useCallback(item => queueRef.current.includes(item), []);

  return { queue, add, remove, contains };
};

const INITIAL_CROP = {
  unit: 'px',
  x: 0,
  y: 0,
  aspect: 1,
  height: 0,
  width: 0
};
export const Image = ({
  image: passedImage,
  index,
  onRequestRemove,
  onRequestMakeFeatured,
  onRequestMakeCover,
  onRequestReplaceImage,
  onRequestChangeImageStyle,
  style,
  isFeatured,
  isCover,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [image, setImage] = useState(passedImage);
  const [croppableImageDimensions, setCroppableImageDimensions] = useState({
    width: 0,
    height: 0,
    naturalWidth: 0,
    naturalHeight: 0
  });

  const [crop, setCrop] = useState(INITIAL_CROP);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const hasChanges = !_.isEqual(image.url, image.originalUrl);
  const hasCrop = !_.isEqual(crop, INITIAL_CROP);

  const { attributes, listeners, setNodeRef, transform, transition, items, over, isDragging, setActivatorNodeRef } = useSortable({ id: image.id });

  const dragStyles = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const croppableContainerRef = useRef(null);

  /*
    We use object-fit: contain to make any image size fit in our croppable container.
    However, that makes the image element fit the container's full size, so react-image-crop uses the
    container's full size for the croppable area. This causes the crop to be offset incorrectly,
    and the image frame we're passing in is incorrect.

    This function does the same math that object-fit: contain does to the image element, so we can 
    reverse engineer the exact offset and frame size to pass to the cropImage function.
  */
  const getCroppedImageOffsets = () => {
    const containerSize = croppableContainerRef.current.getBoundingClientRect();
    const { naturalWidth, naturalHeight } = croppableImageDimensions;

    const scale = Math.min(containerSize.width / naturalWidth, containerSize.height / naturalHeight);

    const renderedWidth = naturalWidth * scale;
    const renderedHeight = naturalHeight * scale;

    return {
      x: (containerSize.width - renderedWidth) / 2,
      y: (containerSize.height - renderedHeight) / 2,
      frameSize: {
        width: renderedWidth,
        height: renderedHeight
      }
    };
  };

  const handleCropImageLoaded = image => {
    setCroppableImageDimensions({
      width: image.width,
      height: image.height,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight
    });
  };

  useEffect(() => {
    if (needsUpdate) {
      try {
        onRequestReplaceImage?.(image);
        setNeedsUpdate(false);
      } catch (error) {
        cogoToast.error(`Error saving image edits: ${error}`);
      }
    }
  }, [needsUpdate]);

  const handleRequestRotateImage = async () => {
    try {
      const newImage = await flipImage(image.url);
      setImage(prev => ({ ...prev, url: newImage.url }));
      setNeedsUpdate(true);
    } catch (error) {
      cogoToast.error(`Error flipping image: ${error}`);
    }
  };

  const handleRequestUpscaleImage = async () => {
    try {
      const newImage = await prettifyImage(getSmartImage(image.url), { skipBackground: true, addFrame: false });
      setImage(prev => ({ ...prev, url: newImage.url }));
      setNeedsUpdate(true);
    } catch (error) {
      cogoToast.error(`Error upscaling image: ${error}`);
    }
  };

  const handleRequestRemoveBackground = async () => {
    try {
      const newImage = await prettifyImage(getSmartImage(image.url), { forceBackground: true });
      setImage(prev => ({ ...prev, url: newImage.url }));
      setNeedsUpdate(true);
    } catch (error) {
      cogoToast.error(`Error removing background: ${error}`);
    }
  };

  const handleRequestCropImage = async () => {
    if (!hasCrop) {
      return;
    }

    try {
      const offset = getCroppedImageOffsets();

      const cropWithoutOffset = {
        ...crop,
        x: crop.x - offset.x,
        y: crop.y - offset.y
      };

      const newImage = await cropImage(image.url, { ...cropWithoutOffset, frame: offset.frameSize }, { size: 1200 });
      setImage(prev => ({ ...prev, url: newImage.url }));
      onRequestReplaceImage?.({ ...image, url: newImage.url });
      setNeedsUpdate(true);

      setCrop(INITIAL_CROP);
    } catch (err) {
      cogoToast.error(`Error cropping image: ${err?.message}`);
    }
  };

  const handleRequestRevertToOriginal = () => {
    setImage(prev => ({
      ...prev,
      url: prev.originalUrl
    }));
    setNeedsUpdate(true);
  };

  const handleImageLoad = e => {
    const { naturalWidth, naturalHeight } = e.target;
    setImageDimensions({ width: naturalWidth, height: naturalHeight });
  };

  const handleRequestChange = async change => {
    switch (change) {
      case 'prettify':
        await handleRequestRemoveBackground();
        break;
      case 'rotate':
        await handleRequestRotateImage();
        break;
      case 'revert':
        await handleRequestRevertToOriginal();
        break;
      case 'crop':
        await handleRequestCropImage();
        break;
      case 'upscale':
        await handleRequestUpscaleImage();
        break;
      default:
        throw new Error(`Unknown change type: ${change.type}`);
    }
  };

  const changeQueue = useQueue(handleRequestChange);

  const getImageDimensionsClassName = () => {
    if (imageDimensions.width < 800 || imageDimensions.height < 800) {
      return 'small';
    }

    if (imageDimensions.width < 1000 || imageDimensions.height < 1000) {
      return 'medium';
    }

    return 'large';
  };

  return (
    <>
      <div className={cn({ isOver: items.indexOf(over?.id) === index, isDragging }, 'image-container')}>
        <div ref={setNodeRef} className={cn({ isDragging }, 'draggable-image')} style={dragStyles} {...attributes}>
          <button ref={setActivatorNodeRef} className={cn({ isDragging }, 'drag-handle')} {...listeners} />

          <img src={getSmartImage(image.url)} alt='product' className={cn({ fitCover: style === 'cover' })} onLoad={handleImageLoad} />

          <div className={cn('image-dimensions', getImageDimensionsClassName())}>
            {imageDimensions.width} x {imageDimensions.height}
          </div>

          <div className='bottom-left-buttons'>
            <div className='image-number'>{index + 1}</div>
            <button
              className={cn('image-style-button', { unset: style === null })}
              onClick={() => onRequestChangeImageStyle?.(image.id, style === 'cover' ? 'contain' : 'cover')}
              disabled={disabled}
            >
              {style === 'cover' ? <IoExpandSharp size={16} /> : <IoContractSharp size={16} />}
            </button>

            <div className='quick-edit-buttons'>
              <button className='quick-edit' onClick={() => changeQueue.add('prettify')} disabled={disabled || changeQueue.contains('prettify')}>
                {changeQueue.contains('prettify') && <Loader variant='circle' size={16} className='loader' />}
                Prettify
              </button>

              <button className='quick-edit' onClick={() => changeQueue.add('rotate')} disabled={disabled || changeQueue.contains('rotate')}>
                {changeQueue.contains('rotate') && <Loader variant='circle' size={16} className='loader' />}
                Rotate
              </button>

              <button className='quick-edit' onClick={() => changeQueue.add('upscale')} disabled={disabled || changeQueue.contains('upscale')}>
                {changeQueue.contains('upscale') && <Loader variant='circle' size={16} className='loader' />}
                Upscale
              </button>
            </div>
          </div>

          <button className='remove-image' onClick={() => onRequestRemove?.(image.id)} disabled={disabled}>
            <IoCloseSharp size={12} />
          </button>

          <button className='edit-image' onClick={() => setIsEditing(true)} disabled={disabled}>
            Edit
          </button>

          <div className='metadata-buttons'>
            <button className={cn({ active: isFeatured })} onClick={() => onRequestMakeFeatured?.(image.id)} disabled={disabled}>
              Preview
            </button>
            <button className={cn({ active: isCover })} onClick={() => onRequestMakeCover?.(image.id)} disabled={disabled}>
              Primary
            </button>
          </div>
        </div>
      </div>

      {isEditing && (
        <Modal title='Edit Image' close={() => setIsEditing(false)} className='image-editor-modal'>
          <div className='image-editor-container' ref={croppableContainerRef}>
            <ReactCrop src={getSmartImage(image.url)} onImageLoaded={handleCropImageLoaded} aspect={1} crop={crop} ruleOfThirds onChange={setCrop} />
          </div>

          <p className='image-editor-instructions'>Drag on the image to crop</p>

          <div className='image-editor-actions'>
            <button className='editor-action' onClick={() => changeQueue.add('rotate')} disabled={changeQueue.contains('rotate')}>
              <IoRepeatOutline size={20} />
              {changeQueue.contains('rotate') && <Loader variant='circle' size={16} className='loader' />}
              Rotate
            </button>

            <button className='editor-action' onClick={() => changeQueue.add('prettify')} disabled={changeQueue.contains('prettify')}>
              <IoColorWandSharp size={20} />
              {changeQueue.contains('prettify') && <Loader variant='circle' size={16} className='loader' />}
              Prettify
            </button>

            <button className='editor-action' onClick={() => changeQueue.add('upscale')} disabled={changeQueue.contains('upscale')}>
              <IoExpandSharp size={20} />
              {changeQueue.contains('upscale') && <Loader variant='circle' size={16} className='loader' />}
              Upscale
            </button>

            <button className='editor-action' onClick={() => changeQueue.add('crop')} disabled={!hasCrop || changeQueue.contains('crop')}>
              <IoCropOutline size={20} />
              {changeQueue.contains('crop') && <Loader variant='circle' size={16} className='loader' />}
              Crop
            </button>

            <button className='editor-action' onClick={() => changeQueue.add('revert')} disabled={!hasChanges}>
              <IoRefreshOutline size={20} />
              {changeQueue.contains('revert') && <Loader variant='circle' size={16} className='loader' />}
              Revert to original
            </button>

            <button className='save-image-edits' onClick={() => setIsEditing(false)}>
              Close
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};
